/**
 * Módulo del tutorial: Cuánto vale tu ganado (KAN-232).
 *
 * El modal de valuación de Administración: el rodeo entero convertido a
 * plata, categoría por categoría, con los precios de mercado de referencia
 * — en pesos o en dólares, para hablar el idioma del que presta.
 *
 * Anclas: menu-administracion, admin-valuo-detalles (ℹ️ de Valuación
 * Animales), admin-stocks (Inventario).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const valuacionModule: TutorialModuleDef = {
  id: 'valuacion',
  title: 'El valor de tu ganado',
  description: 'Tu rodeo entero convertido a plata: valuación por categoría con precios de mercado, en pesos o dólares — lista para el banco o el socio.',
  emoji: '💵',
  image: 'images/tutorial/valuacion.png',
  permission: { module: 'financial', action: 'view' },
  grupo: 'negocio',
  route: '/administracion',
  steps: [
    {
      id: 'concepto-valuacion',
      route: '/tutorial',
      text: '"¿Cuánto tenés puesto en el campo?" — la pregunta del banco y del socio, contestada al toque: tu rodeo valuado con precios de mercado, en pesos o dólares.',
      narration:
        'Y ahora, la pregunta que te hacen en el banco y en la mesa familiar: cuánto tenés puesto en el campo. Acá la respuesta está a un toque: el rodeo entero valuado con precios de mercado, categoría por categoría, en pesos o en dólares. El campo entero convertido a un número, y bien fundamentado.',
    },
    {
      id: 'ir-a-administracion-valuo',
      target: '[data-ar-guide="menu-administracion"]',
      label: 'Administración (menú Finanzas)',
      text: 'La valuación vive en "Administración" (menú Finanzas): la card de Inventario muestra ya el total arriba de todo.',
      narration:
        'Andá al menú, sección Finanzas, y tocá Administración. En la card de Inventario ya te asoma el total de la Valuación de Animales arriba de todo: el número entero, sin entrar a ningún lado. Pero lo bueno está adentro.',
    },
    {
      id: 'abrir-valuacion',
      route: '/administracion',
      target: '[data-ar-guide="admin-valuo-detalles"]',
      label: 'ℹ️ Detalles de Valuación',
      advanceOnClick: false,
      text: 'Tocá el ℹ️ al lado de "Valuación Animales": se abre el desglose completo, categoría por categoría.',
      narration:
        'Tocá la i con el circulito que está al lado del total de Valuación de Animales, y se abre el desglose completo: cuánto vale tu categoría de cría, la de invernada, los toros, cada una con su cuenta. El rodeo entero en una tabla, como el rematador lo leería.',
    },
    {
      id: 'precios-de-mercado',
      route: '/administracion',
      text: 'Los valores usan los precios de mercado de referencia (los mismos de la sección Precios) — y podés verlos en pesos o en dólares.',
      narration:
        'Y de dónde salen los precios? De los precios de mercado de referencia, los mismos que ves en la sección de Precios de hacienda, actualizados solos. Nada de números de fantasía. Y con un toque lo ves todo en pesos o en dólares, para hablar el idioma del que presta o del que invierte.',
    },
    {
      id: 'cierre-valuacion',
      route: '/administracion',
      target: '[data-ar-guide="admin-valuo-detalles"]',
      label: 'Valuación, siempre a mano',
      advanceOnClick: false,
      text: 'Y para el día a día, la valuación vive acá mismo: arriba asoma el total ("Valuación Animales") y con la ℹ️ ves el desglose completo. Tu hacienda, en números que se defienden solos.',
      narration:
        'Y para el día a día, no hace falta entrar a ningún laberinto: acá en Administración, arriba de todo, ya te asoma el total de la Valuación de Animales, y con este botoncito de información desplegás el desglose completo por categoría. Cuando el banco o el socio pregunte cuánto tenés puesto en hacienda, abrís la app y el número está ahí, con respaldo. Tu campo, en números que se defienden solos.',
    },
  ],
  completionCheck: async () => false, // se completa haciendo el recorrido
};
